import { Check, ArrowUpRight, Sparkles } from "lucide-react";

const plans = [
    {
        name: "Starter",
        tag: "For teams testing paid channels",
        volume: "From 50 leads / month",
        desc: "Ideal for boutique brokerages and single-project developers exploring a paid-lead engine for the first time.",
        features: [
            "Minimum 50 verified leads / month",
            "One micro-market focus",
            "Standard qualification filters",
            "Email + WhatsApp lead delivery",
            "Weekly performance reports",
            "Onboarding in under 7 days",
        ],
        cta: "Request pricing",
        highlight: false,
    },
    {
        name: "Growth",
        tag: "Most chosen · Scaling developers",
        volume: "200 – 1,000 leads / month",
        desc: "For developers running active launches across 2–3 micro-markets who want a lead machine that pays for itself.",
        features: [
            "Multi-market lead sourcing",
            "AI-powered intent scoring",
            "Native CRM integrations (LeadSquared, Zoho, Salesforce)",
            "Dedicated account partner",
            "Bi-weekly strategy calls",
            "Site-visit incentive playbook",
        ],
        cta: "Request pricing",
        highlight: true,
    },
    {
        name: "Signature",
        tag: "Enterprise · Pan-India programmes",
        volume: "1,000+ leads / month",
        desc: "White-glove, bespoke lead programmes for enterprise developers with multi-city, multi-project ambitions.",
        features: [
            "Pan-India coverage across tier-1 & tier-2",
            "Full-funnel attribution dashboard",
            "White-glove sales enablement",
            "Priority sales enablement support",
            "Quarterly business reviews with founders",
            "Dedicated NRI + international desk",
        ],
        cta: "Talk to founders",
        highlight: false,
    },
];

export default function Pricing() {
    return (
        <section id="pricing" data-testid="pricing-section" className="py-24 sm:py-32 px-6 sm:px-10 lg:px-16 border-t border-white/5">
            <div className="max-w-7xl mx-auto">
                <div className="max-w-2xl mb-10">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="h-px w-10 bg-[#D4AF37]" />
                        <span className="overline">Engagement Tiers</span>
                    </div>
                    <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight leading-[1.05]">
                        Three ways to <span className="italic text-[#D4AF37]">work together</span>.
                    </h2>
                    <p className="mt-6 text-white/60 max-w-lg">
                        We tailor commercials to your city, project mix, and monthly volume. Pick the tier that best
                        describes where you are today — we'll take it from there.
                    </p>
                </div>

                {/* Minimum threshold callout */}
                <div
                    data-testid="minimum-leads-callout"
                    className="mb-12 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-6 py-5"
                >
                    <div className="shrink-0 h-11 w-11 grid place-items-center border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37]">
                        <Sparkles size={18} strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                        <div className="overline !text-[#D4AF37]/90">Our Starting Point</div>
                        <p className="mt-1 font-serif-display text-xl sm:text-2xl text-white leading-tight">
                            Every engagement begins at a minimum of{" "}
                            <span className="text-[#D4AF37] italic">50 verified leads per month</span>.
                        </p>
                        <p className="mt-2 text-sm text-white/60">
                            Below this, the quality controls and dedicated attention that make Lead Krafts worth
                            partnering with become harder to sustain.
                        </p>
                    </div>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                    {plans.map((p, i) => (
                        <div
                            key={p.name}
                            data-testid={`plan-${p.name.toLowerCase()}`}
                            className={`p-8 lg:p-10 relative flex flex-col ${
                                p.highlight ? "bg-[#D4AF37] text-black" : "hol-card"
                            }`}
                        >
                            {p.highlight && (
                                <div className="absolute -top-3 left-8 bg-black text-[#D4AF37] px-3 py-1 text-[10px] uppercase tracking-[0.25em] font-semibold">
                                    Most chosen
                                </div>
                            )}

                            <div className={`text-[10px] uppercase tracking-[0.24em] ${p.highlight ? "text-black/60" : "text-[#D4AF37]"}`}>
                                {p.tag}
                            </div>
                            <h3 className="mt-4 font-serif-display text-5xl leading-none">{p.name}</h3>

                            {/* Volume badge — replaces price */}
                            <div
                                data-testid={`plan-volume-${i}`}
                                className={`mt-5 inline-flex self-start items-center gap-2 px-3 py-1.5 border text-xs uppercase tracking-widest ${
                                    p.highlight
                                        ? "border-black/25 bg-black/10 text-black"
                                        : "border-[#D4AF37]/30 text-[#D4AF37]"
                                }`}
                            >
                                {p.volume}
                            </div>

                            <p className={`mt-5 text-sm leading-relaxed ${p.highlight ? "text-black/75" : "text-white/60"}`}>
                                {p.desc}
                            </p>

                            <div className={`my-8 h-px ${p.highlight ? "bg-black/15" : "bg-white/10"}`} />

                            <ul className="space-y-3 flex-1">
                                {p.features.map((f) => (
                                    <li key={f} className="flex items-start gap-3 text-sm">
                                        <Check
                                            size={16}
                                            className={p.highlight ? "text-black mt-0.5 shrink-0" : "text-[#D4AF37] mt-0.5 shrink-0"}
                                            strokeWidth={2}
                                        />
                                        <span className={p.highlight ? "text-black/85" : "text-white/80"}>{f}</span>
                                    </li>
                                ))}
                            </ul>

                            <a
                                href="#lead"
                                data-testid={`plan-cta-${i}`}
                                className={`mt-10 group inline-flex items-center justify-center gap-2 py-3.5 font-medium text-sm tracking-wide transition-colors ${
                                    p.highlight
                                        ? "bg-black text-[#D4AF37] hover:bg-black/85"
                                        : "border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
                                }`}
                            >
                                {p.cta}
                                <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </a>
                        </div>
                    ))}
                </div>

                <div className="mt-10 text-center text-xs text-white/40 tracking-wide">
                    All tiers include RERA-compliant campaigns, DPDP-safe data handling, and a 90-day performance
                    review. Volume commitments can be adjusted month-on-month.
                </div>
            </div>
        </section>
    );
}
